
import { useState, useEffect } from "react";
import { Key, Hash, Slack, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface SlackChannelProps {
  onConfigSuccess?: () => void;
}

export default function SlackChannel({ onConfigSuccess }: SlackChannelProps) {
  const [apiKey, setApiKey] = useState("");
  const [appKey, setAppKey] = useState("");
  const [channelId, setChannelId] = useState("");
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (connected && onConfigSuccess) {
      onConfigSuccess();
    }
  }, [connected, onConfigSuccess]);

  const handleConnect = () => {
    if (!apiKey || !appKey || !channelId) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }
    
    setLoading(true);
    
    // Simulate connection
    setTimeout(() => {
      setConnected(true);
      setLoading(false);
      toast.success("Slack conectado com sucesso!");
    }, 1500);
  };

  const resetConnection = () => {
    setConnected(false);
    setApiKey("");
    setAppKey("");
    setChannelId("");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="api-key" className="flex items-center gap-2">
          <Key className="h-4 w-4" />
          API Key
        </Label>
        <Input
          id="api-key"
          type="password"
          placeholder="xoxp-1234567890-1234567890-1234567890-1234567890abcdef"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          disabled={connected || loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="app-key" className="flex items-center gap-2">
          <Slack className="h-4 w-4" />
          App Key
        </Label>
        <Input
          id="app-key"
          type="password"
          placeholder="A1B2C3D4E5F6G7H8I9J0"
          value={appKey}
          onChange={(e) => setAppKey(e.target.value)}
          disabled={connected || loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="channel-id" className="flex items-center gap-2">
          <Hash className="h-4 w-4" />
          ID do Canal
        </Label>
        <Input
          id="channel-id"
          placeholder="C1234567890"
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          disabled={connected || loading}
        />
      </div>

      <div className="pt-2">
        {!connected ? (
          <Button 
            onClick={handleConnect}
            className="w-full"
            disabled={loading}
          >
            {loading ? "Conectando..." : "Conectar ao Slack"}
          </Button>
        ) : (
          <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-md border border-green-200 dark:border-green-900">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
              <span className="font-medium">Slack conectado com sucesso!</span>
            </div>
            <p className="text-sm text-green-600/70 dark:text-green-400/70 mt-1">
              Você já pode receber e enviar mensagens através deste canal.
            </p>
            <Button 
              variant="link" 
              className="mt-2 p-0 text-green-600 dark:text-green-400"
              onClick={resetConnection}
            >
              Resetar conexão
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
